import {
  $getNearestNodeFromDOMNode,
  $getRoot,
  $isElementNode,
  getNearestEditorFromDOMNode,
} from 'lexical';
import type { MouseEvent } from 'react';

/**
 * The block of the editor sitting at the clicked height, or the closest one when
 * the click is above the first block or below the last.
 */
function blockAtHeight(root: HTMLElement, clientY: number): HTMLElement | null {
  const blocks = Array.from(root.children).filter(
    (child): child is HTMLElement => child instanceof HTMLElement
  );
  let closest: HTMLElement | null = null;
  let closestDistance = Number.POSITIVE_INFINITY;
  for (const block of blocks) {
    const { top, bottom } = block.getBoundingClientRect();
    const distance = clientY < top ? top - clientY : clientY > bottom ? clientY - bottom : 0;
    if (distance < closestDistance) {
      closest = block;
      closestDistance = distance;
    }
  }
  return closest;
}

/**
 * `onMouseDown` for the padded frame drawn around a Lexical editor (a subject
 * field, the body's scroll box). A click in that padding hits the frame, not the
 * editable root, so the browser neither focuses the editor nor shows a caret:
 * with a variable chip opening the line, "just before the chip" is exactly that
 * padding. This places the caret like a native input would: at the start of the
 * line for a click in the left gutter, at its end for the right one.
 *
 * Clicks on the frame's children (the editor itself, a trailing button) are left
 * alone.
 */
export function placeCaretFromFieldGutter(event: MouseEvent<HTMLElement>): void {
  if (event.button !== 0 || event.target !== event.currentTarget) return;
  const root = event.currentTarget.querySelector<HTMLElement>('[data-lexical-editor="true"]');
  const editor = root ? getNearestEditorFromDOMNode(root) : null;
  if (!root || !editor || !editor.isEditable()) return;

  event.preventDefault();
  const { left, right } = root.getBoundingClientRect();
  const atStart = event.clientX < (left + right) / 2;
  const block = blockAtHeight(root, event.clientY);

  root.focus({ preventScroll: true });
  editor.update(() => {
    const node = block ? $getNearestNodeFromDOMNode(block) : null;
    const target = $isElementNode(node) ? node : $getRoot();
    if (atStart) target.selectStart();
    else target.selectEnd();
  });
}
